import { isCard, TCard } from "models/CardModel";
import { TRoute } from "models/RouteModel";
import {
  atom,
  atomFamily,
  selector,
  selectorFamily,
  useRecoilCallback,
  useRecoilValue,
  useSetRecoilState,
} from "recoil";

export type TSearchValues = {
  [key: string]: any;
};

const searchValuesState = atomFamily<TSearchValues, TCard["uuid"]>({
  key: "searchValuesState",
  default: {},
});

const searchValuesIdsState = atom<TCard["uuid"][]>({
  key: "SearchValues/Ids",
  default: [],
});

const searchValuesSelector = selectorFamily({
  key: "SearchValues/Selector",
  get:
    (id) =>
    ({ get }) => {
      const atom = get(searchValuesState(id as TCard["uuid"]));
      return atom;
    },
  set:
    (id) =>
    ({ get, set }, newValues) => {
      const newId = id as TCard["uuid"];
      set(searchValuesState(newId), newValues);
      const ids = get(searchValuesIdsState);
      if (!ids.includes(newId)) {
        const newIds = [...ids];
        newIds.push(newId);
        set(searchValuesIdsState, newIds);
      }
    },
});

const allSearchValuesSelector = selector<({ cardId: TCard["uuid"]; values: TSearchValues } | undefined)[]>({
  key: "SearchValues/AllSelector",
  get: ({ get }) => {
    const ids = get(searchValuesIdsState);
    return ids.map((id) => {
      return {
        cardId: id,
        values: get(searchValuesState(id)),
      };
    });
  },
});

export const useSearchValuesState = (id: TCard["uuid"] | TRoute["id"]) => {
  const searchValues = useRecoilValue(searchValuesSelector(id));
  const setSearchValues = useSetRecoilState(searchValuesSelector(id));
  return { searchValues, setSearchValues };
};

// [ToDo] ↑↓ こいつら整理

export const useSearchValuesState2 = () => {
  const getSearchValues = useRecoilCallback(({ snapshot }) => async (card: TCard) => {
    return await snapshot.getPromise(searchValuesState(card.uuid));
  });

  const setSearchValues = useRecoilCallback(
    ({ set, snapshot }) =>
      async (target: TCard | TRoute, values: TSearchValues) => {
        const id = isCard(target) ? target.uuid : target.id;
        set(searchValuesState(id), values);
        const ids = await snapshot.getPromise(searchValuesIdsState);
        if (!ids.includes(id)) {
          const newIds = [...ids];
          newIds.push(id);
          set(searchValuesIdsState, newIds);
        }
      },
  );

  const setSearchValuesIds = useRecoilCallback(({ set }) => (newIds: TCard["uuid"][] | TRoute["id"][]) => {
    set(searchValuesIdsState, (orgIds) => Array.from(new Set([...orgIds, ...newIds])));
  });

  const getAllSearchValues = useRecoilCallback(({ snapshot }) => async () => {
    const values = await snapshot.getPromise(allSearchValuesSelector);
    return values;
  });

  const getSearchValue = useRecoilCallback(({ set, snapshot }) => async (card: TCard) => {
    return await snapshot.getPromise(searchValuesState(card.uuid));
  });

  return {
    getSearchValues,
    setSearchValues,
    setSearchValuesIds,
    getAllSearchValues,
    getSearchValue,
  };
};
